import {
  PrismicCollectionDetailField,
  PrismicLabel,
  PrismicLabelRelationField,
  PrismicTaskInstructions,
  PrismicWorkTask,
} from "types";
import {
  getSliceType,
  LabelConfigurationError,
  parseLabelValueMapping,
} from "./label-relations";
import { prismicClient } from "./prismic-client";

const LABEL_LOAD_TIMEOUT_MS = 15_000;

export { LabelConfigurationError } from "./label-relations";

export function getSliceCollectionIds(slice: {
  slice_type?: string;
  key?: string;
  primary?: { collections?: unknown };
  value?: { "non-repeat"?: { collections?: unknown } };
}): string[] {
  if (getSliceType(slice) !== "collection_details") return [];
  const primary = slice.primary || slice.value?.["non-repeat"];
  const links = primary?.collections;
  if (links == null) return [];
  if (!Array.isArray(links)) {
    throw new LabelConfigurationError(
      "Collection Details must contain a list of collection links."
    );
  }
  return links.flatMap((link) => {
    // Prismic can retain an empty row in a repeatable link field.
    if (
      link &&
      !link.id &&
      !link.isBroken &&
      (link.link_type === "Any" || link.link_type === "Document")
    ) {
      return [];
    }
    if (
      !link ||
      link.link_type !== "Document" ||
      link.type !== "collection_details" ||
      link.isBroken === true ||
      typeof link.id !== "string" ||
      !link.id.trim()
    ) {
      throw new LabelConfigurationError(
        "Collection Details contains a missing or invalid collection link."
      );
    }
    return [link.id];
  });
}

function relationshipDocumentId(
  value: unknown,
  fieldName: string
): string | undefined {
  if (!value || typeof value !== "object") return undefined;

  const relationship = value as {
    id?: unknown;
    type?: unknown;
    link_type?: unknown;
    isBroken?: unknown;
  };
  const isEmpty =
    relationship.link_type === "Any" ||
    (relationship.id === undefined && relationship.isBroken !== true);
  if (isEmpty) return undefined;

  if (
    relationship.isBroken === true ||
    relationship.link_type !== "Document" ||
    relationship.type !== "label" ||
    typeof relationship.id !== "string" ||
    !relationship.id
  ) {
    throw new LabelConfigurationError(
      `${fieldName} contains a missing or invalid reusable label relationship.`
    );
  }

  return relationship.id;
}

function detailFields(
  slices: any[] | null | undefined
): PrismicCollectionDetailField[] {
  return (slices || [])
    .filter((slice) => {
      const type = getSliceType(slice);
      return type === "collection_details" || type === "step_details";
    })
    .flatMap((slice) => slice.items || slice.value?.repeat || []);
}

function collectDocumentIds(fields: PrismicCollectionDetailField[]): string[] {
  const ids = new Set<string>();
  fields.forEach((field, index) => {
    const id = relationshipDocumentId(
      field.label_document,
      `Label field ${index + 1}`
    );
    if (id) ids.add(id);
  });
  return Array.from(ids);
}

async function loadLabelDocuments(
  ids: string[]
): Promise<Map<string, PrismicLabel>> {
  if (ids.length === 0) return new Map();

  const controller = new AbortController();
  const timeout = window.setTimeout(
    () => controller.abort(),
    LABEL_LOAD_TIMEOUT_MS
  );
  try {
    const response = await prismicClient.getByIDs<PrismicLabel>(ids, {
      fetchOptions: { signal: controller.signal },
      pageSize: ids.length,
    });
    const documents = new Map(
      response.results.map((document) => [document.id, document])
    );
    const missing = ids.filter((id) => !documents.has(id));
    if (missing.length > 0) {
      throw new LabelConfigurationError(
        `Reusable label document${
          missing.length === 1 ? "" : "s"
        } ${missing.join(", ")} could not be loaded.`
      );
    }
    return documents;
  } catch (error) {
    if (error instanceof LabelConfigurationError) throw error;
    if (controller.signal.aborted) {
      throw new LabelConfigurationError(
        "Reusable label documents timed out while loading."
      );
    }
    throw new LabelConfigurationError(
      `Reusable label documents could not be loaded: ${
        error instanceof Error ? error.message : String(error)
      }`
    );
  } finally {
    window.clearTimeout(timeout);
  }
}

function labelData(
  relationship: unknown,
  documents: Map<string, PrismicLabel>,
  fieldName: string
): { labelId: string; dropdownOptions: string } | undefined {
  const documentId = relationshipDocumentId(relationship, fieldName);
  if (!documentId) return undefined;

  const document = documents.get(documentId);
  if (!document) {
    throw new LabelConfigurationError(`${fieldName} could not be resolved.`);
  }
  const labelId = document.data.label_id?.toString().trim();
  if (!labelId) {
    throw new LabelConfigurationError(
      `${fieldName} links to a label document without a Label ID.`
    );
  }
  return {
    labelId,
    dropdownOptions: document.data.dropdown_options?.toString() || "",
  };
}

function resolveField(
  field: PrismicCollectionDetailField,
  documents: Map<string, PrismicLabel>,
  fieldName: string
): PrismicCollectionDetailField {
  const linked = labelData(field.label_document, documents, fieldName);
  const relatedLabelId = field.related_to?.toString().trim();
  if (!linked && !relatedLabelId) return field;
  if (
    linked &&
    field.label_type === "dropdown" &&
    !linked.dropdownOptions.trim()
  ) {
    throw new LabelConfigurationError(
      `${fieldName} is a dropdown but its reusable label has no options.`
    );
  }
  return {
    ...field,
    ...(linked
      ? {
          label_id: linked.labelId,
          dropdown_options: linked.dropdownOptions,
          _linked_label: true,
        }
      : {}),
    ...(relatedLabelId ? { _related_label_id: relatedLabelId } : {}),
  } as PrismicCollectionDetailField;
}

function resolveRelation(
  relation: PrismicLabelRelationField,
  index: number
): PrismicLabelRelationField {
  const baseLabelId = relation.base_label_id?.toString().trim();
  const proposedLabelId = relation.proposed_label_id?.toString().trim();
  if (!baseLabelId || !proposedLabelId) {
    throw new LabelConfigurationError(
      `Label relation ${index + 1} is incomplete. Both label IDs are required.`
    );
  }

  const mappings = parseLabelValueMapping(
    relation.value_mapping,
    `Label relation ${index + 1} mapping`
  );

  return {
    ...relation,
    base_label_id: baseLabelId,
    proposed_label_id: proposedLabelId,
    value_mapping: mappings
      .map(({ baseValue, proposedValue }) => `${baseValue}:${proposedValue}`)
      .join("\n"),
  } as PrismicLabelRelationField;
}

function resolveSlices(
  slices: any[] | null | undefined,
  documents: Map<string, PrismicLabel>
): any[] {
  let relationIndex = 0;
  let fieldIndex = 0;
  return (slices || []).map((slice) => {
    const type = getSliceType(slice);
    if (!slice.slice_type && slice.key) {
      slice = {
        ...slice,
        slice_type: type,
        primary: slice.primary || slice.value?.["non-repeat"] || {},
        items: slice.items || slice.value?.repeat || [],
      };
    }
    if (type === "collection_details" || type === "step_details") {
      return {
        ...slice,
        items: (slice.items || []).map((field: PrismicCollectionDetailField) =>
          resolveField(field, documents, `Label field ${++fieldIndex}`)
        ),
      };
    }
    if (type === "label_relation") {
      return {
        ...slice,
        items: (slice.items || []).map((relation: PrismicLabelRelationField) =>
          resolveRelation(relation, relationIndex++)
        ),
      };
    }
    return slice;
  });
}

export async function resolveTaskInstructionLabels(
  data: PrismicTaskInstructions["data"]
): Promise<PrismicTaskInstructions["data"]> {
  const slices = data.instruction_content || [];
  const fields = detailFields(slices as any[]);
  const documents = await loadLabelDocuments(collectDocumentIds(fields));
  return {
    ...data,
    instruction_content: resolveSlices(slices as any[], documents) as any,
  };
}

export async function resolveWorkTaskLabels(
  data: PrismicWorkTask["data"]
): Promise<PrismicWorkTask["data"]> {
  const fields = [...(data.collection_details || [])];
  const sequences = ((data.rtms_sequences || []) as any[]).map((slice) =>
    !slice.slice_type && slice.key
      ? {
          ...slice,
          slice_type: getSliceType(slice),
          primary: slice.primary || slice.value?.["non-repeat"] || {},
          items: slice.items || slice.value?.repeat || [],
        }
      : slice
  );
  const sequenceFields = sequences.flatMap((slice) =>
    slice.slice_type === "rtms_sequence" ? slice.items || [] : []
  );
  const documents = await loadLabelDocuments(
    collectDocumentIds([...fields, ...sequenceFields])
  );
  return {
    ...data,
    rtms_sequences: sequences.map((slice) =>
      slice.slice_type === "rtms_sequence"
        ? {
            ...slice,
            items: (slice.items || []).map(
              (field: PrismicCollectionDetailField, index: number) =>
                resolveField(
                  field,
                  documents,
                  `Sequence label field ${index + 1}`
                )
            ),
          }
        : slice
    ) as any,
    collection_details: fields.map((field, index) =>
      resolveField(field, documents, `Shared label field ${index + 1}`)
    ) as any,
    label_relations: resolveSlices(
      data.label_relations as any[],
      documents
    ) as any,
  };
}

export async function resolveSessionMetadataFields(
  fields: PrismicCollectionDetailField[]
) {
  const documents = await loadLabelDocuments(collectDocumentIds(fields));
  return fields.map((field, index) =>
    resolveField(field, documents, `Session label field ${index + 1}`)
  );
}
